import { useReducer } from 'react';
import type { CouponData } from 'components/new-appointment/Coupon';
import type { Service, StaffOption } from 'types/models';
import actions, { REDUCER_NEW_APT_ACTIONS, SetCustomerInfoPayload, SetStaffDatePayload } from './index.actions';

export interface BookingCustomerInfo {
  phone: string;
  name: string;
  id: string | number | null;
}

export interface NewAppointmentState {
  customer: BookingCustomerInfo;
  coupon: CouponData | null;
  selectedServices: Service[];
  selectedStaff: StaffOption | null;
  isRequestStaff: boolean;
  timeSlot: string | null;
  selectedDate: Date;
  note: string;
  group: number;
}

export type NewAptReducerAction =
  | { type: typeof REDUCER_NEW_APT_ACTIONS.SET_CUSTOMER_INFO; payload?: SetCustomerInfoPayload }
  | { type: typeof REDUCER_NEW_APT_ACTIONS.SET_SERVICES; payload: Service[] }
  | { type: typeof REDUCER_NEW_APT_ACTIONS.SET_STAFF_DATE; payload: SetStaffDatePayload }
  | { type: typeof REDUCER_NEW_APT_ACTIONS.SET_TIME_SLOT; payload: string | null }
  | { type: typeof REDUCER_NEW_APT_ACTIONS.SET_NOTE; payload: string }
  | { type: typeof REDUCER_NEW_APT_ACTIONS.SET_GROUP_VALUE; payload: number }
  | { type: typeof REDUCER_NEW_APT_ACTIONS.RESET };

export const initialReducerData: NewAppointmentState = {
  customer: {
    phone: '',
    name: '',
    id: null,
  },
  coupon: null,
  selectedServices: [],
  selectedStaff: null,
  isRequestStaff: false,
  timeSlot: null,
  selectedDate: new Date(),
  note: '',
  group: 1,
};

function reducer(state: NewAppointmentState, action: NewAptReducerAction): NewAppointmentState {
  if (action.type === REDUCER_NEW_APT_ACTIONS.SET_CUSTOMER_INFO) {
    return actions.setCustomerInfo(state, action.payload);
  }
  if (action.type === REDUCER_NEW_APT_ACTIONS.SET_SERVICES) {
    return actions.setServices(state, action.payload);
  }
  if (action.type === REDUCER_NEW_APT_ACTIONS.SET_STAFF_DATE) {
    return actions.setStaffDate(state, action.payload);
  }
  if (action.type === REDUCER_NEW_APT_ACTIONS.SET_TIME_SLOT) {
    return actions.setTimeSlot(state, action.payload);
  }
  if (action.type === REDUCER_NEW_APT_ACTIONS.SET_NOTE) {
    return actions.setNote(state, action.payload);
  }
  if (action.type === REDUCER_NEW_APT_ACTIONS.SET_GROUP_VALUE) {
    return actions.setGroupValue(state, action.payload);
  }
  if (action.type === REDUCER_NEW_APT_ACTIONS.RESET) {
    return actions.reset(state);
  }
  throw Error('Unknown action.');
}

const useNewAptReducer = () => {
  const [state, dispatch] = useReducer(reducer, initialReducerData);
  const actions = {
    setCustomerInfo: (payload?: SetCustomerInfoPayload) => dispatch({ type: REDUCER_NEW_APT_ACTIONS.SET_CUSTOMER_INFO, payload }),
    setServices: (payload: Service[]) => dispatch({ type: REDUCER_NEW_APT_ACTIONS.SET_SERVICES, payload }),
    setStaffDate: (payload: SetStaffDatePayload) => dispatch({ type: REDUCER_NEW_APT_ACTIONS.SET_STAFF_DATE, payload }),
    setTimeSlot: (payload: string | null) => dispatch({ type: REDUCER_NEW_APT_ACTIONS.SET_TIME_SLOT, payload }),
    setNote: (payload: string) => dispatch({ type: REDUCER_NEW_APT_ACTIONS.SET_NOTE, payload }),
    setGroupValue: (payload: number) => dispatch({ type: REDUCER_NEW_APT_ACTIONS.SET_GROUP_VALUE, payload }),
    reset: () => dispatch({ type: REDUCER_NEW_APT_ACTIONS.RESET }),
  };
  return ({
    state, actions,
  })
}

export default useNewAptReducer;
